import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, AlertTriangle, FileText, FileSignature } from "lucide-react";

/**
 * Small admin-only widget that surfaces finalized-document coverage per org.
 *
 * Only renders for `org_admin` members of the current org — the endpoint
 * itself is `require_org_admin`-gated, so a 403 for non-admins is
 * transparently swallowed and the widget stays hidden. No storage
 * paths, URLs, or hashes are exposed here or in the backend response.
 */
export default function FinalizedDocsWidget() {
  const { currentOrgId, profile } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!currentOrgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await apiFetch(`/admin/finalized-documents/summary?organization_id=${currentOrgId}`);
        if (!cancelled) { setSummary(s); setLoading(false); }
      } catch (_) {
        // 403 for non-admins — don't render the widget at all
        if (!cancelled) { setHidden(true); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrgId, profile?.id]);

  if (hidden) return null;
  if (loading) return null;
  if (!summary) return null;

  const c = summary.contracts || {};
  const i = summary.invoices || {};
  const missing = (c.missing_finalized || 0) + (i.missing_finalized || 0);
  const attention = missing > 0;

  const Row = ({ icon: Icon, label, canonical, backfill, missingCount, onClick, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-4 w-full text-left px-4 py-3 rounded-xl hover:bg-[#FAF8F5] transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-[#FDE9D8] text-[#E8712C] flex items-center justify-center flex-shrink-0">
        <Icon size={16} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-[#57534E] mt-0.5">
          {canonical} canonical · {backfill} legacy backfill
        </div>
      </div>
      <div
        className={`text-xs px-2.5 py-1 rounded-full font-medium ${
          missingCount > 0 ? "bg-[#FDDCDC] text-[#B02222]" : "bg-[#E8F0E4] text-[#1F6B36]"
        }`}
        data-testid={`${testid}-missing`}
      >
        {missingCount > 0 ? `${missingCount} missing` : "all snapshotted"}
      </div>
    </button>
  );

  return (
    <div
      className="bg-white rounded-2xl border border-[#E8E2D9]"
      data-testid="finalized-docs-widget"
    >
      <div className="px-5 py-3 border-b border-[#E8E2D9] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#2C3A29]" strokeWidth={1.75} />
          <h3 className="font-display font-semibold text-sm tracking-tight">
            Record-integrity health
          </h3>
        </div>
        {attention && (
          <span className="flex items-center gap-1 text-xs text-[#B02222]" data-testid="finalized-docs-attention">
            <AlertTriangle size={13} strokeWidth={1.75} />
            {missing} record{missing === 1 ? "" : "s"} need attention
          </span>
        )}
      </div>
      <div className="p-2 divide-y divide-[#F5EDE4]">
        <Row
          icon={FileSignature}
          label="Signed contracts"
          canonical={c.canonical_signed || 0}
          backfill={c.legacy_backfill || 0}
          missingCount={c.missing_finalized || 0}
          onClick={() => navigate(
            (c.missing_finalized || 0) > 0
              ? "/app/contracts?finalized=missing"
              : "/app/contracts?status=signed"
          )}
          testid="finalized-row-contracts"
        />
        <Row
          icon={FileText}
          label="Issued invoices"
          canonical={i.canonical_issued || 0}
          backfill={i.legacy_backfill || 0}
          missingCount={i.missing_finalized || 0}
          onClick={() => navigate(
            (i.missing_finalized || 0) > 0
              ? "/app/invoices?finalized=missing"
              : "/app/invoices?status=sent"
          )}
          testid="finalized-row-invoices"
        />
      </div>
      <div className="px-5 py-2 border-t border-[#F5EDE4] text-[10px] text-[#A8A29E]">
        Counts only · no document contents, storage paths, or hashes exposed.
      </div>
    </div>
  );
}
